import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlayIcon } from 'lucide-react';
import { getProductBySlug } from '../data/products';
import { ProductSpecs } from '../components/products/ProductSpecs';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
interface ProductDetailPageProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductDetailPage({
  onRequestQuote
}: ProductDetailPageProps) {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const [activeMedia, setActiveMedia] = useState<{
    type: 'image' | 'video';
    url: string;
  } | null>(null);
  if (!product) {
    return <Navigate to="/products" replace data-id="element-758" />;
  }
  const hasGallery = product.gallery && product.gallery.length > 0;
  return <main className="min-h-screen bg-dark-bg" data-id="element-759">
      {/* Back Link */}
      <div className="bg-dark-surface border-b-2 border-dark-border" data-id="element-760">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-761">
          <Link to="/products" className="inline-flex items-center text-gray-400 hover:text-brand-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow rounded-industrial" data-id="element-762">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-763" />
            Nazad na proizvode
          </Link>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-dark-surface border-b-2 border-dark-border" data-id="element-764">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-765">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-766">
            <div className="space-y-4" data-id="element-767">
              <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-8 flex items-center justify-center min-h-[400px] lg:min-h-[500px] relative" data-id="element-768">
                <img src={product.image} alt={`${product.name} ${product.model}`} className="w-full h-auto object-contain max-h-[450px]" data-id="element-769" />
              </div>

              {/* Gallery Thumbnails */}
              {hasGallery && <div className="grid grid-cols-4 gap-4" data-id="element-770">
                  {product.gallery?.map((media, idx) => <button key={idx} onClick={() => setActiveMedia(media)} className="relative aspect-square bg-dark-bg border-2 border-dark-border rounded-industrial overflow-hidden hover:border-brand-yellow transition-colors group" data-id="element-771">
                      {media.type === 'image' ? <img src={media.url} alt="Gallery item" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-772" /> : <div className="w-full h-full flex items-center justify-center bg-gray-900 group-hover:bg-gray-800 transition-colors" data-id="element-773">
                          <PlayIcon className="w-8 h-8 text-brand-yellow" data-id="element-774" />
                        </div>}
                    </button>)}
                </div>}
            </div>

            <div className="pt-4" data-id="element-775">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" data-id="element-776">
                {product.name}
              </h1>
              <p className="text-2xl text-gray-400 mb-6" data-id="element-777">{product.model}</p>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-778">
                {product.description}
              </p>
              <div className="mb-8" data-id="element-779">
                <div className="flex items-baseline gap-3" data-id="element-780">
                  <span className="text-5xl font-black text-brand-yellow" data-id="element-781">
                    €{product.price.toLocaleString()}
                  </span>
                  <span className="text-base text-gray-400" data-id="element-782">bez PDV</span>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-783">
                  Sa PDV: €{Math.round(product.price * 1.2).toLocaleString()} ·
                  Rok isporuke: 2 meseca od potvrde porudžbine.
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-784">
                Zatražite ponudu
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-dark-bg" data-id="element-785">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-786">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-787">
            Ključne karakteristike
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-788">
            {product.highlights.map(highlight => <div key={highlight} className="flex items-center space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-4" data-id="element-789">
                <CheckIcon className="w-6 h-6 text-brand-yellow flex-shrink-0" data-id="element-790" />
                <span className="text-white font-semibold" data-id="element-791">{highlight}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-12 bg-dark-surface" data-id="element-792">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-793">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-794">
            Tehničke specifikacije
          </h2>
          <ProductSpecs specs={product.specs} data-id="element-795" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-dark-bg" data-id="element-796">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-797">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-798">
            Funkcije i prednosti
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-799">
            {product.features.map(feature => <div key={feature} className="flex items-start space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-6" data-id="element-800">
                <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-801" />
                <span className="text-gray-300" data-id="element-802">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-surface border-t-2 border-dark-border" data-id="element-803">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-804">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-805">
            Spremni za poručivanje?
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-806">
            Kontaktirajte nas za detaljnu ponudu i rok isporuke.
          </p>
          <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-807">
            Zatražite ponudu
          </Button>
        </div>
      </section>

      {/* Media Modal */}
      <Modal isOpen={!!activeMedia} onClose={() => setActiveMedia(null)} title={product.name} data-id="element-808">
        <div className="bg-dark-bg rounded-industrial overflow-hidden flex items-center justify-center min-h-[50vh]" data-id="element-809">
          {activeMedia?.type === 'image' ? <img src={activeMedia.url} alt="Gallery full" className="max-w-full max-h-[80vh] object-contain" data-id="element-810" /> : activeMedia?.type === 'video' ? <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-[80vh]" data-id="element-811" /> : null}
        </div>
      </Modal>
    </main>;
}